import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useGalleryStore } from '../../stores/galleryStore';
import { UnityProvider, useUnity } from '../../contexts/UnityContext';
import LoadingScreen from '../ui/LoadingScreen';
import GalleryHeader from './GalleryHeader';
import ArtistSidebar from './ArtistSidebar';
import PieceViewer from './PieceViewer';
import ErrorMessage from '../ui/ErrorMessage';
import UnitySurface from './UnitySurface';
import { BottomProgressBar, SwitchTo3DModal } from '../ui/ExperienceOverlays';

function OverlayOrchestrator({
  viewMode,
  setViewMode,
  unityMounted,
  autoSwitchTo3D,
  setAutoSwitchTo3D
}: {
  viewMode: '2d' | '3d';
  setViewMode: (mode: '2d' | '3d') => void;
  unityMounted: boolean;
  autoSwitchTo3D: boolean;
  setAutoSwitchTo3D: (value: boolean) => void;
}) {
  const { loadingProgress, isUnityReady, lastUnityError } = useUnity();
  const [showSwitchPrompt, setShowSwitchPrompt] = useState(false);

  useEffect(() => {
    if (!unityMounted || !isUnityReady || viewMode !== '2d') return;

    if (autoSwitchTo3D) {
      setShowSwitchPrompt(false);
      setAutoSwitchTo3D(false);
      setViewMode('3d');
    } else {
      setShowSwitchPrompt(true);
    }
  }, [autoSwitchTo3D, isUnityReady, setAutoSwitchTo3D, setViewMode, unityMounted, viewMode]);

  return (
    <>
      {/* Keep the gallery usable in 2D while Unity loads hidden in the background. */}
      {unityMounted && viewMode === '2d' && !isUnityReady && !lastUnityError && (
        <BottomProgressBar progress={loadingProgress} />
      )}

      {/* Prompt to switch once Unity is ready */}
      {unityMounted && showSwitchPrompt && (
        <SwitchTo3DModal
          onConfirm={() => {
            setShowSwitchPrompt(false);
            setViewMode('3d');
          }}
          onDismiss={() => setShowSwitchPrompt(false)}
        />
      )}

    </>
  );
}

export default function GalleryPage() {
  const { user, galleryId, preferredViewMode } = useAuthStore();
  const {
    collection,
    isLoading,
    error,
    loadGallery,
    selectedPieceId,
    getCurrentPiece
  } = useGalleryStore();

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'2d' | '3d'>('2d');
  const shouldAutoLoad3D = preferredViewMode !== '2d';
  const [unityMounted, setUnityMounted] = useState(shouldAutoLoad3D);
  const [autoSwitchTo3D, setAutoSwitchTo3D] = useState(shouldAutoLoad3D);

  // Get current piece using the function
  const currentPiece = getCurrentPiece();

  const handleViewModeChange = (mode: '2d' | '3d') => {
    if (mode === '3d') {
      setUnityMounted(true);
      setAutoSwitchTo3D(true);
      setViewMode('2d');
      return;
    }
    setAutoSwitchTo3D(false);
    setViewMode(mode);
  };

  useEffect(() => {
    if (user && galleryId && user.collectionId) {
      loadGallery(galleryId, user.collectionId);
    }
  }, [user, galleryId, loadGallery]);

  // Close mobile sidebar when piece is selected
  useEffect(() => {
    if (selectedPieceId && isMobileSidebarOpen) {
      setIsMobileSidebarOpen(false);
    }
  }, [selectedPieceId, isMobileSidebarOpen]);

  if (isLoading) {
    return <LoadingScreen message="Loading your gallery..." />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!collection) {
    return <LoadingScreen message="Preparing your experience..." />;
  }

  return (
    <UnityProvider pieceId={selectedPieceId || undefined} viewMode={viewMode}>
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Header */}
        <GalleryHeader
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        />

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden relative">

          {/* Mobile Sidebar Backdrop */}
          {isMobileSidebarOpen && (
            <div
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setIsMobileSidebarOpen(false)}
            />
          )}
          {/* Artist Sidebar */}
          <div className="hidden md:flex w-80 bg-white border-r border-gray-200 flex-col">
            <ArtistSidebar />
          </div>

          {/* Mobile Sidebar */}
          <motion.div
            initial={{ x: -320 }}
            animate={{
              x: isMobileSidebarOpen ? 0 : -320
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden fixed inset-y-0 left-0 z-50 w-80 bg-white border-r border-gray-200 flex flex-col"
          >
            {/* Mobile Close Button */}
            <div className="md:hidden flex justify-end p-4">
              <button
                onClick={() => setIsMobileSidebarOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <ArtistSidebar />
          </motion.div>

          {/* Main Viewing Area */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className={`flex-1 flex flex-col relative ${viewMode === '2d' ? 'bg-gray-100' : ''}`}
          >
            {/* Unity surface sits behind viewer UI inside main viewing area */}
            {unityMounted && <UnitySurface viewMode={viewMode} hidden={viewMode === '2d'} />}

            {currentPiece ? (
              <PieceViewer
                piece={currentPiece}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
              />
            ) : (
              <div className="flex-1 relative flex items-center justify-center text-gray-600">
                {/* Tutorial overlay for empty 2D state */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Arrow to artist sidebar (left) */}
                  <div className="absolute top-24 left-24 hidden md:block">
                    <svg width="140" height="40" viewBox="0 0 140 40" fill="none">
                      <line x1="130" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth="2"/>
                      <polygon points="25,15 25,25 15,20" fill="currentColor"/>
                    </svg>
                  </div>
                </div>
                <div className="text-center px-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Start exploring</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    1) Click an artist on the left to view their pieces.
                  </p>
                  <p className="text-sm text-gray-600">
                    2) Click a piece to view it here.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <OverlayOrchestrator
        viewMode={viewMode}
        setViewMode={setViewMode}
        unityMounted={unityMounted}
        autoSwitchTo3D={autoSwitchTo3D}
        setAutoSwitchTo3D={setAutoSwitchTo3D}
      />
    </UnityProvider>
  );
}
